import { z } from 'zod';
import { TournamentSeedingMethodSchema } from './tournament.js';
import type { TournamentDivisionFormat, TournamentSeedingMethod } from './tournament.js';

/**
 * F41 Part B — In-Play Layer (brackets · scoring · standings · live) frozen API
 * contracts. The shared error codes live in `tournament.ts TOURNAMENT_ERROR_CODES`
 * (the `BRACKET_LOCKED`/`INSUFFICIENT_PARTICIPANTS`/`LEAGUE_WINDOW_TOO_SHORT`/
 * `MATCH_*`/`SCORE_*` codes) — this module ships only the in-play enums, the
 * score/seed/generate/finalize Zod input schemas, and the live read shapes.
 *
 * A division's `format` drives a real engine (D14): single_elimination = a seeded
 * knockout tree (byes to top seeds, optional third-place match); round_robin =
 * everyone-plays-everyone → standings; league = round-robin scheduled across the
 * date range → standings. A division may be single-stage or two-stage
 * (group → knockout). Organizer + admin enter scores (D15); a finalized match
 * advances the bracket / recomputes standings + feeds F31 (D16). The public live
 * view is a 60-sec poll (D17).
 *
 * See plans/41_F41_tournament_event_listings.md §17. Wave-2 Track F + the design
 * track develop against these contracts — DO NOT rename/drop/add fields without
 * escalating to the orchestrator first (contract-amendment protocol).
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/** A bracket stage's kind (§17.3 — supports two-stage group→knockout). */
export const TOURNAMENT_STAGE_KINDS = ['group', 'knockout', 'league'] as const;
export type TournamentStageKind = (typeof TOURNAMENT_STAGE_KINDS)[number];
export const TournamentStageKindSchema = z.enum(TOURNAMENT_STAGE_KINDS);

/** Stage / round lifecycle (§17.3). */
export const TOURNAMENT_STAGE_STATUSES = ['pending', 'active', 'completed'] as const;
export type TournamentStageStatus = (typeof TOURNAMENT_STAGE_STATUSES)[number];
export const TournamentStageStatusSchema = z.enum(TOURNAMENT_STAGE_STATUSES);

export const TOURNAMENT_ROUND_STATUSES = ['pending', 'active', 'completed'] as const;
export type TournamentRoundStatus = (typeof TOURNAMENT_ROUND_STATUSES)[number];
export const TournamentRoundStatusSchema = z.enum(TOURNAMENT_ROUND_STATUSES);

/**
 * Match status (§17.3). `bye` = a structural auto-advance (no opponent — never
 * feeds F31); `walkover` = a present opponent advances on a no-show (also no F31
 * feed). Only a `final` match with two present participants feeds F31 (§17.10).
 */
export const TOURNAMENT_MATCH_STATUSES = [
  'scheduled',
  'in_progress',
  'final',
  'bye',
  'walkover',
] as const;
export type TournamentMatchStatus = (typeof TOURNAMENT_MATCH_STATUSES)[number];
export const TournamentMatchStatusSchema = z.enum(TOURNAMENT_MATCH_STATUSES);

/** The two sides of a match (single-elim progression slot / roster snapshot side). */
export const TOURNAMENT_MATCH_SIDES = ['a', 'b'] as const;
export type TournamentMatchSide = (typeof TOURNAMENT_MATCH_SIDES)[number];
export const TournamentMatchSideSchema = z.enum(TOURNAMENT_MATCH_SIDES);

// ─── Bounds / constants ──────────────────────────────────────────────────────

/**
 * Public live-view server-cache TTL + client poll interval (D17, §17.5/§17.10).
 * F61 (B16) — the TTL matches the 60-sec poll (was 30 s, so a lone viewer
 * always missed). Score entry and bracket generation bust the snapshot, so
 * results still show on the next poll.
 */
export const TOURNAMENT_LIVE_CACHE_TTL_SECONDS = 60;
export const TOURNAMENT_LIVE_POLL_INTERVAL_SECONDS = 60;
/** A division needs >= this many confirmed registrations to generate a bracket (M5). */
export const TOURNAMENT_MIN_CONFIRMED_TO_GENERATE = 2;
/** The sport-median seed for an unrated registrant under skill seeding (M12-seed, D18). */
export const TOURNAMENT_UNRATED_SEED_VALUE = 3.5;

// ─── Zod input schemas — seeding · generation · scoring ──────────────────────

/**
 * One seed entry for `POST /me/tournaments/:id/divisions/:divId/seed`. `position`
 * is the 1-based seed slot; `registrationId` is the seeded entrant. Manual seeding
 * supplies the full ordered list; random/skill seeding ignores the list and the
 * service computes it.
 */
export const TournamentSeedEntrySchema = z
  .object({
    registrationId: z.string().uuid(),
    position: z.number().int().positive(),
  })
  .strict();
export type TournamentSeedEntry = z.infer<typeof TournamentSeedEntrySchema>;

/**
 * `POST /me/tournaments/:id/divisions/:divId/seed` body (D18). `method` selects
 * the seeding strategy; `seeds` is required+honoured for `manual`, ignored for
 * `random`/`skill`.
 */
export const SeedDivisionInputSchema = z
  .object({
    method: TournamentSeedingMethodSchema,
    seeds: z.array(TournamentSeedEntrySchema).optional(),
  })
  .strict()
  .refine((v) => v.method !== 'manual' || (v.seeds !== undefined && v.seeds.length > 0), {
    message: 'manual seeding requires a non-empty seeds list',
    path: ['seeds'],
  });
export type SeedDivisionInput = z.infer<typeof SeedDivisionInputSchema>;

/**
 * `POST /me/tournaments/:id/divisions/:divId/bracket` body — generate the
 * bracket/schedule from the confirmed roster (M9 idempotent; `BRACKET_LOCKED` once
 * any `final` match exists). `twoStage` opts into a group → knockout structure
 * (round-robin/league only); `knockoutTopN` selects how many advance to the
 * knockout stage.
 */
export const GenerateBracketInputSchema = z
  .object({
    /** Not supported yet (FIX 4.4, N4): `true` → 422 `BRACKET_FORMAT_UNSUPPORTED`. */
    twoStage: z.boolean().default(false),
    knockoutTopN: z.number().int().positive().optional(),
    /**
     * FIX 4.4 (§10.8-F3) — a `manual` division's order (1st = top seed). The
     * seed call can't store it, so generate takes it again; without it the
     * engine falls back to registration order.
     */
    seeds: z.array(TournamentSeedEntrySchema).optional(),
    /** FIX 4.4 (§10.8-F3) — the `seedRandomKey` the seed call returned, so a `random` bracket matches the preview. */
    seedRandomKey: z.string().min(1).max(200).optional(),
  })
  .strict();
export type GenerateBracketInput = z.infer<typeof GenerateBracketInputSchema>;

/**
 * A sport-aware match score (§17.3 — stored as `tournament_matches.score` jsonb).
 * `sets` is the per-set score pair; `winnerSide` is the resolved winner. The
 * service validates the score is consistent with the sport's rules at finalize.
 */
export const TournamentScoreSetSchema = z
  .object({
    a: z.number().int().min(0),
    b: z.number().int().min(0),
  })
  .strict();
export type TournamentScoreSet = z.infer<typeof TournamentScoreSetSchema>;

export const TournamentScoreSchema = z
  .object({
    sets: z.array(TournamentScoreSetSchema).min(1),
    winnerSide: TournamentMatchSideSchema.optional(),
  })
  .strict();
export type TournamentScore = z.infer<typeof TournamentScoreSchema>;

/**
 * `POST /me/tournaments/:id/matches/:matchId/score` body — save an editable DRAFT
 * score (organizer/admin only, D15). Draft scores are unlimited-editable until
 * finalize (N3).
 */
export const EnterMatchScoreInputSchema = z
  .object({
    score: TournamentScoreSchema,
  })
  .strict();
export type EnterMatchScoreInput = z.infer<typeof EnterMatchScoreInputSchema>;

/**
 * `POST /me/tournaments/:id/matches/:matchId/finalize` body — finalize the match
 * (one-way; advances single-elim, recomputes standings, feeds F31). The
 * `winnerRegistrationId` resolves the winner; the optional `score` finalizes a
 * draft inline. `participantsPresent` flags which sides showed (a walkover/forfeit
 * has one absent side and never feeds F31, §17.10).
 */
export const FinalizeMatchInputSchema = z
  .object({
    winnerRegistrationId: z.string().uuid(),
    score: TournamentScoreSchema.optional(),
    participantAPresent: z.boolean().default(true),
    participantBPresent: z.boolean().default(true),
  })
  .strict();
export type FinalizeMatchInput = z.infer<typeof FinalizeMatchInputSchema>;

/**
 * `POST /me/tournaments/:id/matches/:matchId/schedule` body — set a match's
 * scheduled time + host/court. `hostId` is an APPROVED `tournament_venue_hosts`
 * row (§18.2 — scheduling fails at a non-approved host with `HOST_NOT_APPROVED`).
 */
export const ScheduleMatchInputSchema = z
  .object({
    scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
    hostId: z.string().uuid().nullable().optional(),
    courtOrLocation: z.string().max(200).nullable().optional(),
  })
  .strict();
export type ScheduleMatchInput = z.infer<typeof ScheduleMatchInputSchema>;

/**
 * `POST /admin/tournaments/:id/matches/:matchId/correct` body — the admin-only
 * post-finalization correction path (forbidden once a downstream match in the same
 * division/stage is `final` — `SCORE_CORRECTION_FORBIDDEN`, §17.2). Reverses +
 * re-applies via F31's admin-override path. Mandatory reason (audited).
 */
export const CorrectMatchScoreInputSchema = z
  .object({
    winnerRegistrationId: z.string().uuid(),
    score: TournamentScoreSchema,
    reason: z.string().min(1).max(500),
  })
  .strict();
export type CorrectMatchScoreInput = z.infer<typeof CorrectMatchScoreInputSchema>;

// ─── Read-side projections — live bracket / standings ────────────────────────

/** A thin participant reference rendered on a match card / standings row. */
export type MatchParticipantRef = {
  readonly registrationId: string;
  /** Display name (player display name or team name). */
  readonly displayName: string;
  /** Seed slot when known (null pre-seeding / for byes). */
  readonly seed: number | null;
};

/**
 * One match (§17.3). `participantA`/`participantB` are null for a bye / a
 * TBD-awaiting-prior-round slot. `score` is the sport-aware draft/final score.
 * `nextMatchId`/`nextMatchSlot` carry the single-elim progression.
 */
export type MatchDetail = {
  readonly id: string;
  readonly roundId: string;
  readonly divisionId: string;
  readonly participantA: MatchParticipantRef | null;
  readonly participantB: MatchParticipantRef | null;
  readonly status: TournamentMatchStatus;
  readonly score: TournamentScore | null;
  readonly winnerRegistrationId: string | null;
  readonly nextMatchId: string | null;
  readonly nextMatchSlot: TournamentMatchSide | null;
  readonly hostId: string | null;
  readonly courtOrLocation: string | null;
  readonly scheduledAt: string | null;
  readonly isThirdPlace: boolean;
};

/** One round (§17.3 — "Quarter-Final" / "Week 3") + its matches. */
export type RoundDetail = {
  readonly id: string;
  readonly stageId: string;
  readonly ordinal: number;
  readonly name: string | null;
  readonly scheduledStart: string | null;
  readonly scheduledEnd: string | null;
  readonly status: TournamentRoundStatus;
  readonly matches: ReadonlyArray<MatchDetail>;
};

/** One stage (§17.3 — group / knockout / league) + its rounds. */
export type StageDetail = {
  readonly id: string;
  readonly divisionId: string;
  readonly kind: TournamentStageKind;
  readonly ordinal: number;
  readonly format: TournamentDivisionFormat;
  readonly status: TournamentStageStatus;
  readonly rounds: ReadonlyArray<RoundDetail>;
};

/** One round-robin/league standings row (§17.3), recomputed on finalization. */
export type StandingsRow = {
  readonly stageId: string;
  readonly registrationId: string;
  readonly displayName: string;
  readonly played: number;
  readonly won: number;
  readonly lost: number;
  readonly drawn: number;
  readonly points: number;
  readonly scoreFor: number;
  readonly scoreAgainst: number;
  readonly scoreDiff: number;
  readonly rank: number | null;
};

/**
 * The public live-view payload for one division (§17.5 — 60-sec poll). `format`
 * selects which face the client renders: single_elimination → the `BracketView`
 * tree; round_robin/league → the `StandingsTable`. `stages` carries the bracket
 * structure; `standings` is populated for round-robin/league stages.
 * `championRegistrationId` is set once the division completes.
 */
export type BracketView = {
  readonly divisionId: string;
  readonly tournamentId: string;
  readonly format: TournamentDivisionFormat;
  readonly seedingMethod: TournamentSeedingMethod | null;
  readonly bracketGeneratedAt: string | null;
  readonly championRegistrationId: string | null;
  readonly stages: ReadonlyArray<StageDetail>;
  readonly standings: ReadonlyArray<StandingsRow>;
  /** ISO timestamp the live snapshot was assembled (drives the client poll diff). */
  readonly asOf: string;
};
